// XoWS — WebSocket client for the X&O page. Mirror of `LudoWS` scoped
// to `/api/xo/ws`. Kept as a separate class rather than a shared
// generic so Ludo's tested WS behaviour stays untouched; if we later
// want a cross-game shared client, both classes can migrate at once.
import { getAccessToken, api } from '@baaligames/shared-ui/lib/api';

type Listener = (msg: any) => void;
export type WSStatus = 'connecting' | 'open' | 'reconnecting' | 'lost';
type StatusFn = (s: WSStatus) => void;

export class XoWS {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusFn>();
  private closed = false;
  private reconnecting = false;
  private pingTimer: number | null = null;
  private authFailedOnce = false;

  connect() {
    this.closed = false;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.emitStatus(this.reconnecting ? 'reconnecting' : 'connecting');
    this.ws = new WebSocket(`${proto}://${location.host}/api/xo/ws`);
    this.ws.addEventListener('open', () => {
      const wasReconnecting = this.reconnecting;
      this.emitStatus('open');
      this.auth(wasReconnecting).finally(() => { this.reconnecting = false; });
    });
    this.ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg && msg.type === 'error' && msg.code === 'auth_failed') {
          this.authFailedOnce = true;
        }
        for (const l of this.listeners) l(msg);
      } catch {}
    });
    this.ws.addEventListener('close', (ev) => {
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      if (ev && (ev as CloseEvent).code === 4002) this.authFailedOnce = true;
      if (!this.closed) {
        this.reconnecting = true;
        this.emitStatus('lost');
        setTimeout(() => this.connect(), 3000);
      }
    });
    this.ws.addEventListener('error', () => {/* swallow */});
  }

  private async auth(isReconnect = false) {
    const needsRefresh = !getAccessToken() || isReconnect || this.authFailedOnce;
    if (needsRefresh) {
      await api.tryRefresh();
      this.authFailedOnce = false;
    }
    const token = getAccessToken();
    if (!token || !this.ws) return;
    this.ws.send(JSON.stringify({ type: 'authenticate', token }));
    this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
  }

  onStatus(fn: StatusFn) {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }
  private emitStatus(s: WSStatus) { for (const fn of this.statusListeners) fn(s); }

  send(msg: any) {
    if (!this.ws || this.ws.readyState !== 1) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }
  on(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  close() {
    this.closed = true;
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    this.ws?.close();
    this.ws = null;
  }
}
